import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, Phone, MapPin, Building2, Send, Loader2, CheckCircle } from 'lucide-react'
import api from '../lib/api'
import { toast } from '../lib/toast'
import { useSeoHead } from '../lib/seo'
import { pagePath } from '../lib/localizedUrl'

export default function ContactPage() {
  const { t } = useTranslation()
  // Self-canonical (cs /kontakt, ostatní jazyky /contact) + hreflang → prerenderovaná stránka
  // se indexuje sama za sebe, ne jako duplicita homepage.
  useSeoHead({
    title: t('contact_page.meta_title', { defaultValue: 'Kontakt, SEO Tools' }),
    description: t('contact_page.subtitle', { defaultValue: 'Máte dotaz, námět nebo potřebujete pomoc? Napište nám, ozveme se co nejdřív.' }),
    hreflangPath: (l) => pagePath('contact', l),
  })
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [website, setWebsite] = useState('') // honeypot — lidé nevyplní
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      await api.post('/contact', { name, email, subject, message, website })
      setSent(true)
      toast.success(t('contact_page.sent_toast', { defaultValue: 'Zpráva odeslána. Děkujeme!' }))
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('common.error_network'))
    } finally {
      setLoading(false)
    }
  }

  const inputClass = 'w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm transition'

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
          {t('contact_page.title', { defaultValue: 'Kontakt' })}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
          {t('contact_page.subtitle', { defaultValue: 'Máte dotaz, námět nebo potřebujete pomoc? Napište nám, ozveme se co nejdřív.' })}
        </p>
      </div>

      <div className="grid md:grid-cols-5 gap-8">
        {/* Údaje o provozovateli */}
        <div className="md:col-span-2 space-y-6">
          <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-6">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-primary-500" />
              {t('contact_page.operator', { defaultValue: 'Provozovatel' })}
            </h2>
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
              <p className="font-semibold text-gray-900 dark:text-white">La Luce s.r.o.</p>
              <p>{t('contact_page.ico', { defaultValue: 'IČO' })}: 02041081</p>
              <p>{t('contact_page.registered', { defaultValue: 'Zapsaná v obchodním rejstříku vedeném Městským soudem v Praze, sp. zn. C 214783.' })}</p>
              <p>{t('contact_page.data_box', { defaultValue: 'Datová schránka' })}: yygy2ic</p>
              <p className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                Hartigova 2454/237, 130 00 Praha 3
              </p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                <a href="mailto:info@seo-solution.cz" className="hover:text-primary-500 transition">info@seo-solution.cz</a>
              </p>
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                <a href="tel:+420739651039" className="hover:text-primary-500 transition">+420 739 651 039</a>
              </p>
            </div>
          </div>
        </div>

        {/* Formulář */}
        <div className="md:col-span-3">
          {sent ? (
            <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {t('contact_page.success_title', { defaultValue: 'Zpráva odeslána' })}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('contact_page.success_desc', { defaultValue: 'Děkujeme, ozveme se vám co nejdříve na zadaný e-mail.' })}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contact_page.name', { defaultValue: 'Jméno' })}
                  </label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contact_page.email', { defaultValue: 'E-mail' })} *
                  </label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('contact_page.subject', { defaultValue: 'Předmět' })}
                </label>
                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('contact_page.message', { defaultValue: 'Zpráva' })} *
                </label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} className={inputClass} required minLength={10} />
              </div>
              {/* Honeypot — skryté pole proti botům */}
              <input
                type="text" value={website} onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1} autoComplete="off" aria-hidden="true"
                className="hidden" name="website"
              />
              <button type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-primary-600 to-primary-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {t('contact_page.send', { defaultValue: 'Odeslat zprávu' })}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
